"use client";

import { useState, useEffect } from "react";
import { homePageContent } from "./content";
import { Box, Heading, HStack, Stack } from "@chakra-ui/react";

const heroSubtitles: readonly string[] = homePageContent.hero.subtitles;
const SUBTITLE_BUTTON_LABEL = "Show next homepage slogan";

type RotatingSubtitleProps = {
  defaultSubtitle?: string;
  intervalMs?: number;
};

export default function RotatingSubtitle({
  defaultSubtitle = heroSubtitles[0],
  intervalMs = 3000,
}: RotatingSubtitleProps) {
  const [currentSubtitle, setCurrentSubtitle] = useState(defaultSubtitle);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      if (heroSubtitles.length <= 1) return;

      // Get random subtitle different from current
      let newIndex: number;
      do {
        newIndex = Math.floor(Math.random() * heroSubtitles.length);
      } while (heroSubtitles[newIndex] === currentSubtitle);

      setCurrentSubtitle(heroSubtitles[newIndex]);
    }, intervalMs);

    return () => clearInterval(interval);
  }, [currentSubtitle, isPaused, intervalMs]);

  const handleClick = () => {
    if (heroSubtitles.length <= 1) return;

    // Get next subtitle (cycling through the array)
    const currentIndex = heroSubtitles.indexOf(currentSubtitle);
    const nextIndex = (currentIndex + 1) % heroSubtitles.length;
    setCurrentSubtitle(heroSubtitles[nextIndex]);
  };
  const currentSubtitleIndex = heroSubtitles.indexOf(currentSubtitle);

  return (
    <Box position="relative" textAlign="center" mx="auto" maxW="3xl">
      <Heading as="h2" fontWeight="medium">
        <Stack
          as="button"
          // type="button"
          aria-label={SUBTITLE_BUTTON_LABEL}
          mx="auto"
          px="0"
          py="0"
          gap="2"
          borderWidth="0"
          bg="transparent"
          color="moveon.publicFont"
          lineHeight={{ base: "short", md: "tall" }}
          cursor="pointer"
          transition="color .15s ease, opacity .15s ease"
          _hover={{
            color: "moveon.primary",
          }}
          _focusVisible={{
            outlineWidth: "2px",
            outlineStyle: "solid",
            outlineColor: "moveon.primary",
            outlineOffset: "3px",
          }}
          onClick={handleClick}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onFocus={() => setIsPaused(true)}
          onBlur={() => setIsPaused(false)}
          title={SUBTITLE_BUTTON_LABEL}
        >
          <Box
            as="span"
            display="flex"
            alignItems="center"
            justifyContent="center"
            // minH={{ base: "[4.5rem]", md: "[3.75rem]" }}
            minH="24"
            fontSize={{ base: "lg", md: "xl" }}
            textAlign="center"
          >
            {currentSubtitle}
          </Box>

          <HStack as="span" justify="center" gap="1.5" aria-hidden="true">
            {heroSubtitles.map((subtitle, index) => (
              <Box
                key={subtitle}
                as="span"
                w={index === currentSubtitleIndex ? "5" : "1.5"}
                h="1.5"
                rounded="full"
                bg={
                  index === currentSubtitleIndex
                    ? "moveon.primary"
                    : "moveon.public.surface.border"
                }
                transition="width .15s ease, background-color .15s ease"
              />
            ))}
          </HStack>
        </Stack>
      </Heading>
    </Box>
  );
}
